import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast, withLoading } from "@/lib/toast";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { api, BACKEND_URL, readSession } from "@/lib/api";
import { DEFAULT_SLA, applySlaConfig } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

// Store conectado à API ASP.NET Core (Zitec.Api). Mantém a mesma interface usada pelas páginas.
const StoreContext = createContext(null);
const POLL_MS = 15000;

const parseDetails = (h) => {
  try { return { ...h, details: JSON.parse(h.details_json || "{}") }; } catch { return { ...h, details: {} }; }
};

export const StoreProvider = ({ children }) => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [details, setDetails] = useState({});
  const [sla, setSla] = useState(DEFAULT_SLA);
  // Produtos da abertura do chamado (Customização), inclusive os inativos; a abertura filtra os ativos
  const [produtos, setProdutos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const knownIds = useRef(null);
  // notify=true (polling): avisa sobre chamados que chegaram desde a última leitura (ex.: via webhook)
  const loadTickets = useCallback(async (notify = false) => {
    const { data } = await api.get("/tickets");
    const list = data.map((x) => x.ticket);
    if (notify === true && knownIds.current) {
      list.filter((t) => !knownIds.current.has(t.id)).forEach((t) => toast(`Novo chamado ${t.codigo}: ${t.titulo}`));
    }
    knownIds.current = new Set(list.map((t) => t.id));
    setTickets(list);
  }, []);
  const loadUsers = useCallback(async () => setUsers((await api.get("/users")).data), []);
  const loadWebhooks = useCallback(async () => setWebhooks((await api.get("/webhooks")).data), []);
  // Sem a API/tabela de configuração, mantém os prazos padrão
  const loadSla = useCallback(async () => {
    try { const { data } = await api.get("/settings/sla"); applySlaConfig(data); setSla(data); } catch { /* usa DEFAULT_SLA */ }
  }, []);
  const loadProdutos = useCallback(async () => setProdutos((await api.get("/produtos", { params: { todos: true } })).data), []);

  const refresh = useCallback(async () => {
    loadSla();
    loadProdutos().catch(() => { /* sem a tabela de produtos: a abertura avisa que a lista está vazia */ });
    try { await Promise.all([loadTickets(), loadUsers(), loadWebhooks()]); } catch (err) { toast.error(err.message); } finally { setLoaded(true); }
  }, [loadTickets, loadUsers, loadWebhooks, loadSla, loadProdutos]);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  // Atualiza a lista periodicamente para refletir chamados novos (título da aba, menu, dashboard)
  useEffect(() => {
    if (!user) { knownIds.current = null; return undefined; }
    const i = setInterval(() => { loadTickets(true).catch(() => { /* API fora do ar: tenta no próximo ciclo */ }); }, POLL_MS);
    return () => clearInterval(i);
  }, [user, loadTickets]);

  // Detalhe completo (histórico, evidências, comentários) — GET /api/tickets/{id}
  // silent (atualização automática da página do chamado): sem toast e sem apagar o que está na tela se a API falhar;
  // só troca o detalhe se algo mudou, para não redesenhar a conversa a cada ciclo
  const loadTicket = useCallback(async (id, { silent = false } = {}) => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      const full = { ...data.ticket, historico: data.historico.map(parseDetails), evidencias: data.evidencias, comentarios: data.comentarios };
      setDetails((d) => (silent && d[id] && JSON.stringify(d[id]) === JSON.stringify(full) ? d : { ...d, [id]: full }));
      setTickets((ts) => ts.map((t) => (t.id === id ? data.ticket : t)));
      return full;
    } catch (err) {
      if (silent) return null;
      setDetails((d) => ({ ...d, [id]: null }));
      // 404: a própria página mostra "Chamado não foi encontrado"; toast só para erros inesperados
      if (err.status !== 404) toast.error(err.message);
      return null;
    }
  }, []);

  // Tempo real (SignalR, /hubs/chamados): o servidor avisa "ticketChanged" e só o que está na tela é buscado de novo.
  // Sem conexão (proxy sem WebSocket, API fora), seguem valendo as atualizações periódicas
  const [realtime, setRealtime] = useState(false);
  const detailsRef = useRef(details);
  detailsRef.current = details;
  useEffect(() => {
    if (!user) return undefined;
    let timerLista = null;
    const conn = new HubConnectionBuilder()
      .withUrl(`${BACKEND_URL}/hubs/chamados`, { accessTokenFactory: () => readSession()?.token ?? "", withCredentials: false })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();
    conn.on("ticketChanged", ({ ticket_id: id, tipo }) => {
      // Lista (contadores, kanban, aviso de chamado novo): agrupa avisos seguidos numa leitura só
      clearTimeout(timerLista);
      timerLista = setTimeout(() => loadTickets(true).catch(() => { /* tenta no próximo aviso */ }), 400);
      // Detalhe: só se este chamado já foi aberto nesta sessão (página do chamado ou histórico)
      if (tipo !== "excluido" && detailsRef.current[id]) loadTicket(id, { silent: true });
    });
    conn.onreconnecting(() => setRealtime(false));
    // Ao reconectar, pode ter perdido avisos: recarrega o que está na tela
    conn.onreconnected(() => {
      setRealtime(true);
      loadTickets().catch(() => {});
      Object.keys(detailsRef.current).filter((id) => detailsRef.current[id]).forEach((id) => loadTicket(id, { silent: true }));
    });
    conn.onclose(() => setRealtime(false));
    let ativo = true;
    conn.start().then(() => ativo && setRealtime(true)).catch(() => ativo && setRealtime(false));
    return () => { ativo = false; clearTimeout(timerLista); setRealtime(false); conn.stop(); };
  }, [user, loadTickets, loadTicket]);

  // Executa a chamada; em caso de erro mostra o toast e devolve undefined (as páginas checam o retorno)
  // Ações mostram o loading do SweetAlert2 enquanto a API responde
  const run = async (fn, ...reload) => {
    try {
      return await withLoading(async () => {
        const res = await fn();
        await Promise.all(reload.map((r) => r()));
        return res ?? true;
      });
    } catch (err) {
      toast.error(err.message);
      return undefined;
    }
  };

  const afterTicket = (id) => [loadTickets, () => loadTicket(id)];

  const createTicket = (payload) => run(async () => (await api.post("/tickets", payload)).data, loadTickets);
  const transferTicket = (id, { stage, assigned_to, nota }) =>
    run(() => api.post(`/tickets/${id}/transfer`, { stage, assigned_to: assigned_to || null, nota }), ...afterTicket(id));
  const resolveTicket = (id, solucao) =>
    run(() => api.post(`/tickets/${id}/resolve`, { solucao, notificar_solicitante: false }), ...afterTicket(id));
  const reopenTicket = (id) => run(() => api.post(`/tickets/${id}/reopen`), ...afterTicket(id));
  const changePriority = (id, prioridade, nota) => run(() => api.post(`/tickets/${id}/priority`, { prioridade, nota }), ...afterTicket(id));
  // Devolve { ok, slack }: slack = "enviado" / "falhou" quando o chamado tem conversa no Slack (null se não tem)
  // evidencias: arquivos colados/anexados na mensagem ({ name, content_type, data_url }); vão juntos para o Slack
  const addComment = (id, mensagem, interno, evidencias = []) => run(async () => (await api.post(`/tickets/${id}/comment`, { mensagem, interno, evidencias })).data, ...afterTicket(id));
  // Cria a tarefa no Redmine a partir do chamado; devolve { id, url } da tarefa
  const sendToRedmine = (id, payload) => run(async () => (await api.post(`/tickets/${id}/redmine`, payload)).data, ...afterTicket(id));
  const addEvidence = (id, ev) => run(() => api.post(`/tickets/${id}/evidence`, ev), ...afterTicket(id));
  // Exclusão definitiva (a API só aceita de gestor/admin); some da lista e do cache de detalhes
  const deleteTicket = (id) => run(async () => {
    await api.delete(`/tickets/${id}`);
    setDetails((d) => { const { [id]: _removido, ...resto } = d; return resto; });
  }, loadTickets);

  const createUser = (u) => run(async () => (await api.post("/users", u)).data, loadUsers);
  const updateUser = (id, patch) => run(() => api.patch(`/users/${id}`, patch), loadUsers);
  const deleteUser = (id) => run(() => api.delete(`/users/${id}`), loadUsers);
  // Link temporário para o usuário definir a senha: { token, expires_at }
  const createPasswordLink = (id) => run(async () => (await api.post(`/users/${id}/password-link`)).data);
  const sendPasswordLinkEmail = (id) => run(async () => (await api.post(`/users/${id}/password-link/email`)).data);

  const createWebhook = (w) => run(async () => (await api.post("/webhooks", w)).data, loadWebhooks);
  const deleteWebhook = (id) => run(() => api.delete(`/webhooks/${id}`), loadWebhooks);
  const updateWebhookUrl = (id, url_destino) => run(() => api.patch(`/webhooks/${id}`, { url_destino }), loadWebhooks);

  // POST /api/webhooks/inbound/{token} — o mesmo endpoint que as plataformas externas chamam
  const simulateInbound = (token, payload) =>
    run(async () => {
      const { data } = await api.post(`/webhooks/inbound/${token}`, payload);
      return { id: data.ticket_id, codigo: data.ticket_codigo };
    }, loadTickets);

  // Cadastro de produtos (gestor): sem exclusão, inativar tira da lista de abertura
  const createProduto = (nome) => run(async () => (await api.post("/produtos", { nome })).data, loadProdutos);
  const updateProduto = (id, patch) => run(async () => (await api.put(`/produtos/${id}`, patch)).data, loadProdutos);
  const reorderProdutos = (ids) => run(async () => { const { data } = await api.put("/produtos/ordem", { ids }); setProdutos(data); return data; });

  const saveSla = (cfg) => run(async () => { const { data } = await api.put("/settings/sla", cfg); applySlaConfig(data); setSla(data); return data; }, loadTickets);


  return (
    <StoreContext.Provider value={{
      tickets, users, webhooks, details, sla, saveSla, produtos, createProduto, updateProduto, reorderProdutos, refresh, loadTicket, loaded, realtime,
      createTicket, transferTicket, resolveTicket, reopenTicket, deleteTicket, changePriority, addComment, addEvidence, sendToRedmine,
      createUser, updateUser, deleteUser, createPasswordLink, sendPasswordLinkEmail, createWebhook, deleteWebhook, updateWebhookUrl, simulateInbound,
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
